import React, {
  useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import apiFetch from 'utils/apiFetch';
import { random as getRandomColor } from 'utils/colors';
import { TABLET_WIDTH } from 'constants/breakPoints';
import AlertModal from 'components/FeedbackModal';
import ThemeTableBubbles from 'components/ThemeTablesBubbles';
import gridIcon from 'assets/images/icons/grid.png';
import gridOffIcon from 'assets/images/icons/grid-off.png';
import Faces from 'components/Steps/Faces';
import AutocompleteInput from 'components/AutocompleteInput';
import UsersModal from 'components/UsersModal';
import { STATUS_INIT, STATUS_SUCCESS, STATUS_FAILURE } from 'constants/status';
import NoResultsImg from 'assets/images/no_results.svg';
import Loading from 'components/LoadingComponent';
import AffiliationCard from 'components/AffiliationCard';
import {
  NetworkingHallSection, FeedbackModal, CloseIcon, LoadingWrapper,
} from 'components/People/components/styles';

function ElementsHall(props) {
  const {
    elementType, elementId, literals, app, type,
  } = props;
  const { color1 } = app;

  const [elements, setElements] = useState([]);
  const [statusRequest, setStatusRequest] = useState(STATUS_INIT);
  const [selectedTopic, setSelectedTopic] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Errors
  const [errors, setErrors] = useState([]);

  const removeErrors = () => {
    setErrors([]);
  };

  const printErrors = () => {
    if (!errors.length) {
      return null;
    }
    return (
      <AlertModal
        onClose={removeErrors}
        open
        success={false}
        text={errors.join('<br />')}
      />
    );
  };

  // Tags
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Searcher
  const handleSearcher = (value) => {
    const valueId = parseInt(value, 10);
    const newSelectedTags = [...selectedTags];
    if (!_.find(newSelectedTags, tagValue => tagValue === valueId)) {
      newSelectedTags.push(valueId);
    }

    const newTags = _.remove(tags, { id: valueId }).concat(tags);

    setSelectedTags(newSelectedTags);
    setTags(newTags);
  };

  const handleSelectOptionSearcher = (option) => {
    handleSearcher(option.id);
  };

  const printSearcher = () => {
    const options = _.map(tags, tag => ({
      value: tag.id,
      label: tag.name,
    }));
    return <AutocompleteInput type='tag' placeholder={literals.searchElements} onSelectOption={handleSelectOptionSearcher} options={options} async={false} />;
  };

  // Load data
  const searchElements = () => {
    const params = { type };
    if (elementType) {
      params.element_type = elementType;
    }
    if (elementId) {
      params.element_id = elementId;
    }

    setSelectedTopic(0);
    setElements([]);
    setTags([]);
    setStatusRequest(STATUS_INIT);
    setIsLoading(true);
    apiFetch('GET', { endPoint: 'networking-hall/elements' }, params)
      .then((response) => {
        setIsLoading(false);
        const finalTags = [];
        _.forEach(response.data, (topic) => {
          if (typeof topic.related_elements !== 'undefined') {
            _.forEach(topic.related_elements, (element) => {
              if (!_.find(finalTags, { id: element.id })) {
                finalTags.push(element);
              }
            });
          }
        });
        setTags(finalTags);
        setElements(response.data);
        setStatusRequest(STATUS_SUCCESS);
      })
      .catch((error) => {
        setIsLoading(false);
        errors.push(error.response.data.error);
        setErrors([...errors]);
        setStatusRequest(STATUS_FAILURE);
      });
  };

  useEffect(() => {
    searchElements();
  }, [elementType, elementId]);

  // Filter by tags
  useEffect(() => {
    if (!selectedTags.length) {
      window.$('.theme_bubble').removeClass('not_pass_filter');
    } else {
      _.forEach(elements, (topic) => {
        let hasTag = false;
        if (typeof topic.related_elements !== 'undefined') {
          hasTag = true;
          _.forEach(selectedTags, (selectedTag) => {
            if (!_.find(topic.related_elements, { id: selectedTag })) {
              hasTag = false;
            }
          });
        }

        if (hasTag) {
          window.$(`.theme_bubble[data-id=${topic.id}]`).removeClass('not_pass_filter');
        } else {
          window.$(`.theme_bubble[data-id=${topic.id}]`).addClass('not_pass_filter');
        }
      });
    }
  }, [selectedTags]);


  const selectTag = (event) => {
    const id = parseInt(event.currentTarget.getAttribute('data-id'), 10);
    const newSelectedTags = [...selectedTags];
    if (_.find(newSelectedTags, tagId => tagId === id)) {
      _.remove(newSelectedTags, tagId => tagId === id);
    } else {
      newSelectedTags.push(id);
    }
    setSelectedTags(newSelectedTags);
  };

  const printTags = () => {
    return _.map(tags, (tag) => {
      return (
        <div
          className={`tag_filter ${_.find(selectedTags, selectedTag => selectedTag === tag.id) ? 'selected' : ''}`}
          key={tag.id}
          data-id={tag.id}
          onClick={selectTag}
        >
          {tag.name}
        </div>
      );
    });
  };

  // Open Lists
  const [openLists, setOpenLists] = useState([]);
  const expandUserList = (event) => {
    const title = event.currentTarget.getAttribute('data-title');

    const lists = [...openLists];
    if (_.find(lists, t => t === title)) {
      _.remove(lists, t => t === title);
    } else {
      lists.push(title);
    }
    setOpenLists(lists);
  };

  // Select user
  const [selectedUser, setSelectedUser] = useState(null);

  const handleClickUserBubble = (userId) => {
    let users = [];

    _.forEach(elements, (topic) => {
      if (!selectedTopic || selectedTopic === topic.id) {
        users = users.concat(topic.users);
      }
    });
    const user = _.find(users, { id: userId });
    setSelectedUser(user);
  };

  const removeSelectedUser = () => {
    setSelectedUser(null);
  };

  const printSelectedUser = () => {
    if (!selectedUser) {
      return null;
    }

    let users = [];

    _.forEach(elements, (topic) => {
      if (!selectedTopic || selectedTopic === topic.id) {
        users = users.concat(topic.users);
      }
    });

    return (
      <UsersModal
        usersIds={_.map(users, user => user.id)}
        userSelected={selectedUser.id}
        onEnd={removeSelectedUser}
      />
    );
  };

  // Selected topic
  const changeElement = (id) => {
    window.$('.theme_bubble').removeClass('selected');
    window.$(`.theme_bubble[data-id=${id}]`).addClass('selected');
    setSelectedTopic(id);
  };

  const printUsers = () => {
    const users = [];

    _.forEach(elements, (topic) => {
      if (!selectedTopic || selectedTopic === topic.id) {
        _.forEach(topic.users, (user) => {
          if (!_.find(users, { id: user.id })) {
            users.push(user);
          }
        });
      }
    });

    if (!users.length) {
      return null;
    }

    let title = literals.people;
    if (selectedTopic) {
      const topic = _.find(elements, { id: selectedTopic });
      title = `${literals.peopleIn} ${topic.name}`;
    }
    const titleSection = 'users';

    const maxHiddenUsers = window.outerWidth <= TABLET_WIDTH ? 3 : 6;
    const classGrid = _.find(openLists, t => t === titleSection) ? 'in_table' : 'in_line';
    const iconExpand = _.find(openLists, t => t === titleSection) ? gridOffIcon : gridIcon;
    return (
      <div className='list'>
        <div className='header'>
          <div className='title'>{title}</div>
          {maxHiddenUsers < users.length ? (
            <div className='icon_expand' data-title={titleSection} onClick={expandUserList}>
              <img src={iconExpand} alt='' />
            </div>
          ) : null}
        </div>
        <div className={`bubbles ${classGrid}`}>
          <Faces
            options={users}
            onClick={handleClickUserBubble}
            onLongPress={handleClickUserBubble}
          />
        </div>
      </div>
    );
  };


  const printGravityBubbles = () => {
    if (!elements.length) {
      return null;
    }
    const maxPoints = _.maxBy(elements, 'points').points;
    const minPoints = _.minBy(elements, 'points').points;
    const difference = Math.max(maxPoints - minPoints, 1);
    const interval = difference / 3;
    const limitBig = maxPoints - interval;
    const limitMedium = limitBig - interval;

    const finalElements = _.map(elements, (element) => {
      const color = getRandomColor().color;

      let size = 'xs';
      if (element.points >= limitBig) {
        size = 'lg';
      } else if (element.points >= limitMedium) {
        size = 'md';
      }

      const finalTopic = {
        id: element.id,
        name: element.name,
        tags: typeof element.related_elements !== 'undefined' ? _.map(element.related_elements, tag => tag.name) : [],
        color,
        size,
        image: element.image,
      };

      return finalTopic;
    });

    return (
      <div className='theme_tables_wrapper'>
        <ThemeTableBubbles data={finalElements} onClick={changeElement} />
      </div>
    );
  };

  const gravityBubbles = useMemo(() => (
    <div className='gravity_bubbles_wrapper'>{printGravityBubbles()}</div>
  ), [elements]);

  const handleCloseLists = () => {
    setSelectedTopic(0);
  };

  const printSelectedTopic = () => {
    if (!selectedTopic) {
      return null;
    }
    return (
      <AffiliationCard
        open
        onClose={handleCloseLists}
        elementId={selectedTopic}
      />
    );
  };

  const printContent = () => {
    if (statusRequest !== STATUS_SUCCESS) {
      return null;
    }

    if (!elements.length) {
      return (
        <div className='no_elements'>
          <img alt='' src={NoResultsImg} />
          <div>{literals.noElements}</div>
        </div>
      );
    }
    return (
      <div className='content gravity_bubbles fullscreen'>
        {gravityBubbles}
      </div>
    );
  };

  if (isLoading) {
    return (<LoadingWrapper><Loading /></LoadingWrapper>);
  }

  return (
    <NetworkingHallSection className='elements_hall' color={color1}>
      {printErrors()}
      {printSelectedUser()}
      {printSelectedTopic()}
      <div className='search_bar form'>
        <div className='form_question_input'>
          {printSearcher()}
          <div className='filters_tags'>{printTags()}</div>
        </div>
      </div>
      {printContent()}
    </NetworkingHallSection>
  );
}

ElementsHall.propTypes = {
  literals: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  elementType: PropTypes.string,
  elementId: PropTypes.number,
  type: PropTypes.string.isRequired,
};

ElementsHall.defaultProps = {
  elementType: null,
  elementId: null,
};

export default ElementsHall;



// WEBPACK FOOTER //
// ./src/components/ElementsHall/components/index.jsx